import type { ListingListParams } from "@/api/buyers";
import type { ListFilterDef } from "@/hooks/useListFilters";
import { LISTING_FILTERS, type ListingCardFilter, type ListViewPreset } from "./buyerFilters";

// The Listings list's saved views (D5) and the descriptor the page hands to
// `useListFilters`. Beside `buyerFilters.ts` rather than inside it: that file
// holds the vocabularies the four lists SHARE, and a view is one page's own.

/** Days a listing may sit with nobody newly presented. Mirrors the server's
 *  `BuyerCrmTiles.ListingNoBuyerDays`, which computes both the count and the
 *  drill-through. */
export const LISTING_NO_BUYER_DAYS = 30;

/**
 * The three attention reasons, each a SERVER drill-through — the same
 * expression the count in `ListingStatsDto` is computed with, so a chip and the
 * table it narrows to describe one set.
 *
 * <p>🔴 <b>They are OR'd in prose and AND'd on the wire.</b> `ListingListQuery`
 * carries one flag per reason and combines them conjunctively, and the summary
 * row carries neither the agent's email nor a last-presented date, so the union
 * can be computed on NEITHER side. The "Needs attention" view therefore opens on
 * the first reason and offers the other two as chips in the primary row: every
 * request it sends is one the server answers exactly, at the cost of the reader
 * clicking through three reasons rather than reading one merged list. A server
 * `needsAttentionOnly` for listings is the collector for that.</p>
 */
export const LISTING_ATTENTION_REASONS: {
  key: Exclude<ListingCardFilter, "">;
  label: string;
  params: Partial<ListingListParams>;
}[] = [
  { key: "no-agent-email", label: "No agent email", params: { noAgentEmailOnly: true } },
  { key: "no-price", label: "No price stated", params: { noAskingPriceOnly: true } },
  {
    key: "no-buyer",
    label: `No buyer presented in ${LISTING_NO_BUYER_DAYS} days`,
    params: { noBuyerPresentedWithinDays: LISTING_NO_BUYER_DAYS },
  },
];

export const LISTING_VIEWS: ListViewPreset<ListingListParams>[] = [
  { key: "all", label: "All listings", params: {} },
  { key: "in-play", label: "In play", params: { hasSubmissions: true } },
  {
    key: "needs-attention",
    label: "Needs attention",
    params: {},
    description: "No agent email, no price stated, or no buyer presented recently.",
  },
];

export const DEFAULT_LISTING_VIEW = "all";

const LISTING_VIEW_TOKENS = new Set(LISTING_VIEWS.map((v) => v.key));

/** The saved view. URL-only — the toolbar's chips are the control; empty means
 *  "no choice in the URL" and the page falls back to the persisted preference. */
const viewFilter: ListFilterDef<string> = {
  control: "hidden",
  defaultValue: "",
  parse: (raw) => (LISTING_VIEW_TOKENS.has(raw) ? raw : undefined),
};

/** The shared descriptor plus this page's view param. Every URL the shared
 *  descriptor accepted is still accepted, byte for byte. */
export const LISTING_LIST_FILTERS = { ...LISTING_FILTERS, view: viewFilter };
